<template>
  <div>

    <form
      v-if="isLoadedNormal && mesa !== null"
      @submit.prevent="updateMesaWithConfirmation"
    >
      <b-form-group
        label="Player group"
        label-for="player-group-selector"
        label-class="mb-1"
      >
        <b-form-select
          id="player-group-selector"
          v-model="playerGroupId"
          :options="playerGroupOptions"
        />
      </b-form-group>

      <b-form-group
        label="Client min version"
        label-for="client-min-version"
        label-class="mb-1"
      >
        <input id="client-min-version" class="form-control" v-model="clientMinVersionStr" required>
      </b-form-group>

      <hr>

      <div class="mb-4">
        <h5>Start</h5>

        <b-form-datepicker
          v-model="plannedStartDateStr"
          :min="minPlannedStartDateStr"
          :start-weekday="MONDAY"
          :readonly="isStimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedStartTimeStr"
          :readonly="isStimeReadonly"
        />
      </div>

      <div class="mb-4">
        <h5>Finish</h5>

        <b-form-datepicker
          v-model="plannedFinishDateStr"
          :min="minPlannedFinishDateStr"
          :start-weekday="MONDAY"
          :readonly="isFtimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedFinishTimeStr"
          :readonly="isFtimeReadonly"
        />
      </div>

      <div class="mb-3">
        <h5>Duration</h5>
        <p>
          {{durationStr}}
        </p>
      </div>

      <hr>

      <h5 class="mb-1">Join prohibition interval</h5>
      <fieldset class="form-group">
        <duration-input v-model="prohibitionInterval"></duration-input>
        <small id="join-available-until" class="form-text text-muted">Join available until: {{ joinAvailableUntil | getMomentMinStr }} (UTC+3)</small>
      </fieldset>

      <b-button :to="{name: 'mesa_info', params: {mesaId: mesaId}}">Cancel</b-button>

      <b-button
        type="submit"
        :disabled="isReadonly"
        variant="primary"
      >
        Save
      </b-button>

    </form>

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator
      v-if="isLoading"
    />

  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { getPlayerGroupOptionsVerbose } from '../model/event.js';

/**
 * @typedef { import("../model/mesa.js").Mesa } Mesa
 */

import { isMesaEditable      } from '../model/mesa.js';
import { isMesaStimeEditable } from '../model/mesa.js';
import { isMesaFtimeEditable } from '../model/mesa.js';

import { confirmAction                       } from '../service/confirm.js';
import {convertHourToSec, convertStampToDateStr} from '../util.js';
import { convertStampToTimeStr               } from '../util.js';
import { convertMomentToDateStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { convertVersionCodeToStr             } from '../util.js';

import LoadingIndicator from './LoadingIndicator.vue';
import TimeInput        from './TimeInput.vue';
import DurationInput from "@/components/DurationInput.vue";

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'MesaEdit',

  components: {
    DurationInput,
    LoadingIndicator,
    TimeInput,
  },

  props: {
    mesaId: {
      type: Number,
      required: true,
    },
  },

  data() {
    const DEFAULT_PROHIBITION_DURATION_HOURS = 10;
    const DEFAULT_PROHIBITION_DURATION_SEC = convertHourToSec(DEFAULT_PROHIBITION_DURATION_HOURS);

    return {
      MONDAY: 1,

      loadState: LOAD_STATE_INIT,

      /** @type {?Mesa} */
      mesa: null,

      plannedStartDateStr: '',
      minPlannedStartDateStr: '',
      plannedStartTimeStr: '',

      plannedFinishDateStr: '',
      minPlannedFinishDateStr: '',
      plannedFinishTimeStr: '',

      clientMinVersionStr: '',

      prohibitionInterval: DEFAULT_PROHIBITION_DURATION_SEC,

      /** @type {?PlayerGroupId} */
      playerGroupId: null,

      playerGroupOptions: getPlayerGroupOptionsVerbose(),
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.mesa === null) {
        return `Mesa with id ${this.mesaId} does not exist.`;
      }
      return null;
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDateStr, this.plannedStartTimeStr);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDateStr, this.plannedFinishTimeStr);
    },

    /** @returns {?string} */
    durationStr() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return null;
      }

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return null;
      }

      return getDurationStrBetweenMoments(plannedStartMoment, plannedFinishMoment);
    },

    /** @returns {boolean} */
    isReadonly() {
      const mesa = this.mesa;
      if(mesa === null) {
        return true;
      }

      const isEditable = isMesaEditable(mesa.status);
      return !isEditable;
    },

    /** @returns {boolean} */
    isStimeEditable() {
      const mesa = this.mesa;
      if(mesa === null) {
        return false; // data not loaded
      }

      return isMesaStimeEditable(mesa.status);
    },

    /** @returns {boolean} */
    isStimeReadonly() {
      return !this.isStimeEditable;
    },

    /** @returns {boolean} */
    isFtimeEditable() {
      const mesa = this.mesa;
      if(mesa === null) {
        return false;
      }

      return isMesaFtimeEditable(mesa.status);
    },

    /** @returns {boolean} */
    isFtimeReadonly() {
      return !this.isFtimeEditable;
    },

    joinAvailableUntil() {
      return this.plannedFinishSec - this.prohibitionInterval;
    },
  },

  created() {
    this.loadMesa();
  },

  methods: {
    loadMesa() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/mesa/${this.mesaId}/info`;
      HTTP.get(url)
      .then(response => {
        const mesa = response.data.data;
        this.mesa = mesa;

        const now = new Date();
        const nowDateStr = convertMomentToDateStr(now);

        this.plannedStartDateStr     = convertStampToDateStr(mesa.stime);
        this.minPlannedStartDateStr  = nowDateStr;
        this.plannedStartTimeStr     = convertStampToTimeStr(mesa.stime)

        this.plannedFinishDateStr    = convertStampToDateStr(mesa.ftime);
        this.minPlannedFinishDateStr = nowDateStr;
        this.plannedFinishTimeStr    = convertStampToTimeStr(mesa.ftime);

        this.clientMinVersionStr     = convertVersionCodeToStr(mesa.clientMinVersionCode);

        this.prohibitionInterval     = mesa.prohibitionInterval;

        this.playerGroupId = mesa.playerGroupId;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    updateMesaWithConfirmation() {
      this.confirmMesaUpdate(() => {
        this.updateMesa();
      });
    },

    // function onConfirmed()
    confirmMesaUpdate(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to save changes for this mesa {env}?',
        'Save',
        onConfirmed,
      );
    },

    updateMesa() {
      notifier.deleteSenderNotifications(this);

      const playerGroupId = this.playerGroupId;
      if(playerGroupId === null) {
        notifier.showError('Player group must be specified.', {sender: this});
        return;
      }

      const plannedStartSec  = Math.floor(this.plannedStartMoment.getTime() / 1000);
      const plannedFinishSec = Math.floor(this.plannedFinishMoment.getTime() / 1000);

      const trimmedClientMinVersion = this.clientMinVersionStr.trim();
      if(trimmedClientMinVersion.length === 0) {
        notifier.showError('Client min version must be specified.', {sender: this});
        return;
      }

      const data = new FormData();
      data.set('stime', plannedStartSec);
      data.set('ftime', plannedFinishSec);
      data.set('client_min_version', trimmedClientMinVersion);
      data.set('player_group_id',    playerGroupId.toString());
      data.set('prohibition_interval',  this.prohibitionInterval.toString());

      const url = `/mesa/${this.mesaId}`;
      HTTP.post(url, data)
      .then(response => {
        const result = response.data.result.code;

        const RESULT_SUCCESS           = 1;
        const RESULT_SCHEDULE_CONFLICT = 2;

        if(result === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Changes saved.');
          this.$router.push({name: 'mesa_info', params: {mesaId: this.mesaId}});
          return;
        }

        if(result === RESULT_SCHEDULE_CONFLICT) {
          notifier.showError('Can not schedule mesa. Schedule conflict.', {sender: this});
          return;
        }

        notifier.showError(`Unknown result code ${result}.`, {sender: this});
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },
  },
};
</script>
